import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {UPDATE_WEBSITE_SUB_DATA, UpdateWebsiteSubData} from '../actions/website.action';
import * as storeActions from '../actions';
import {
  AddActivity,
  FinishOngoingActivity,
  InitWebsiteProductAction,
  LoadWebsiteProducts,
  UpdateOngoingActivityState
} from '../actions';
import {DbResponse} from "../../shared/interceptors/db-response";
import {filter, first, map, mergeMap, switchMap} from 'rxjs/operators';
import {zip} from 'rxjs';
import {Utils} from "../../shared/utils/utils";
import * as websiteProductActions from '../actions/website-product.action';
import * as tenWebProductAction from '../actions/ten-web-product.action';
import {ProductService} from "../../websites/website/product.service";
import {Website} from "../../websites/website/website";
import {ActivityAction, ActivityStatus, ActivityType, IActivity} from '../../activities/activity';
import {ProductActionService} from "../../websites/website/product-action.service";
import * as activityActions from "../actions/activities.action";
import * as ongoingActivityActions from "../actions/ongoing-activities.action";
import {DbStoreState} from "../reducers";
import {Store} from "@ngrx/store";
import {Product, ProductOrigin} from "../../websites/website/product";
import {ProductStatusActionService} from "../../websites/website/product-status-action.service";
import {TypeUtils} from "../../shared/utils/type-utils";
import {EMPTY} from "rxjs/internal/observable/empty";
import {Config} from "../../config/config";

@Injectable()
export class WebsitePluginEffects {

  public isChildWebsite: boolean;
  updateWebsiteSubData$ = createEffect(() => this._actions$.pipe(
    ofType(UPDATE_WEBSITE_SUB_DATA),
    filter((action: UpdateWebsiteSubData) => action.payload.isActive && !action.payload.isLocalhost),
    switchMap((action) => {
      const _childWebsiteId = action['payload'].isNetwork && !action['payload'].parentId && action['payload'].children && action['payload'].children.length ? action['payload'].children[0] : null;
      return [
        new websiteProductActions.LoadWebsiteProducts(_childWebsiteId || action['payload'].id, action['payload'].isNetwork && action['payload'].parentId),
        new tenWebProductAction.LoadTenWebProducts()
      ];
    })
  ));

  loadWebsiteProducts$ = createEffect(() => this._actions$.pipe(
    ofType(websiteProductActions.LOAD_WEBSITE_PRODUCTS),
    mergeMap((action: LoadWebsiteProducts) => {
      this.isChildWebsite = action.isChildWebsite;
      return zip(
          this._productService.getWebsitePlugins(action.payload).pipe(filter((response: DbResponse) => !!response)),
          this._productService.getWebsiteExtensions(action.payload).pipe(filter((response: DbResponse) => !!response)),
          this._productService.getWebsiteThemes(action.payload).pipe(filter((response: DbResponse) => !!response))
        )
        .pipe(
          map((responseList: DbResponse[]) => {
            const failedResponse: DbResponse = responseList.find((response: DbResponse): boolean => !response.success);
            if (!failedResponse) {
              return new websiteProductActions.LoadWebsiteProductsSuccess({
                websiteId: action.payload,
                pluginEntities: Utils.arrayToEntities(responseList[0].data && responseList[0].data.products ? responseList[0].data.products : [], 'slug'),
                extensionEntities: Utils.arrayToEntities(responseList[1].data && responseList[1].data.products ? responseList[1].data.products : [], 'slug'),
                themeEntities: Utils.arrayToEntities(responseList[2].data && responseList[2].data.products ? responseList[2].data.products : [], 'slug'),
                isChildWebsite: this.isChildWebsite ? this.isChildWebsite : false
              });
            }

            return new websiteProductActions.LoadWebsiteProductsFail(failedResponse.message);
          })
        );
    })
  ));
  initWebsiteProductUploadAction$ = createEffect(() => this._actions$.pipe(
    ofType(websiteProductActions.INIT_WEBSITE_PRODUCT_UPLOAD_ACTION),
    mergeMap((action: InitWebsiteProductAction) => {
      const payload = action.payload;
      if (payload.website && +payload.website.id === Config.demoDomainId) {
        return EMPTY;
      }
      this._productActionService.initProductAction(payload.website, payload.products, payload.action, payload.network)
        .pipe(filter((response: DbResponse) => !!response),
          first())
        .subscribe((response: DbResponse) => {
          if (!response.success) {
            setTimeout(() => {
              action.payload.products.forEach((product: Product) => {
                const activityId: string = payload.website.id.toString() + 'upload' + payload.action;

                this._store.dispatch(new UpdateOngoingActivityState({
                  websiteId: payload.website.id,
                  entity: this.generateProductFailedActivity(activityId, payload.website, product, payload.action, true)
                }));
                this._store.dispatch(new FinishOngoingActivity({
                  websiteId: payload.website.id,
                  entity: this.generateProductFailedActivity(activityId, payload.website, product, payload.action, true)
                }));
              });
            });
          }
        });

      return action.payload.products.map((product: Product) => {
        const activityId: string = payload.website.id.toString() + 'upload' + payload.action;
        const activity: IActivity = this.generateProductUploadActivity(activityId, payload.website, product, payload.action);

        this.simulateActivityProgress(activity);

        return new ongoingActivityActions.UpdateOngoingActivityState({
          websiteId: payload.website.id,
          entity: activity
        });
      });
    })
  ));
  updateWebsiteProductStateActivity$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivityActions.FINISH_ONGOING_ACTIVITY),
    filter((action: FinishOngoingActivity): boolean => (<string[]>[
      ActivityAction.INSTALL,
      ActivityAction.INSTALL_ACTIVATE,
      ActivityAction.INSTALL_ACTIVATE_UPLOAD,
      ActivityAction.UPDATE,
      ActivityAction.ACTIVATE,
      ActivityAction.DEACTIVATE
    ]).includes(action.payload.entity.action)),
    filter((action: FinishOngoingActivity): boolean => !((<string[]>[
      ActivityAction.INSTALL,
      ActivityAction.INSTALL_ACTIVATE,
      ActivityAction.INSTALL_ACTIVATE_UPLOAD
    ]).includes(action.payload.entity.action) && TypeUtils.isEmpty(action.payload.entity.meta))),
    map((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;
      const isInstallAction: boolean = [ActivityAction.INSTALL, ActivityAction.INSTALL_ACTIVATE, ActivityAction.INSTALL_ACTIVATE_UPLOAD].includes(<any>activity.action);
      const isUpdate: boolean = activity.action === ActivityAction.UPDATE;
      const isService: boolean = Product.isService(action.payload.entity.meta.product_data.slug);

      const isFailed: boolean = activity.status === ActivityStatus.FAILED;
      const isTenWebProduct: boolean = activity.meta && activity.meta.product_data && activity.meta && activity.meta.product_data.origin === ProductOrigin.TEN_WEB;
      if (true) {
        this._productStatusActionService.openUnavailableWebsiteDialog(action.payload.websiteId);
      }

      return action;
    }),
    filter((action: FinishOngoingActivity) => action.payload.entity.status === ActivityStatus.DONE),
    map((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;

      return new websiteProductActions.UpdateWebsiteProductState({
        websiteId: activity.websiteId,
        product: activity.meta.info
      });
    })
  ));

  deleteWebsiteProductActivity$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivityActions.FINISH_ONGOING_ACTIVITY),
    filter((action: FinishOngoingActivity): boolean => (<string[]>[
      ActivityAction.DEACTIVATE_DELETE
    ]).includes(action.payload.entity.action) && action.payload.entity.status === ActivityStatus.DONE),
    map((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;

      return new websiteProductActions.DeleteWebsiteProduct({
        websiteId: activity.websiteId,
        product: activity.meta.product_data
      });
    })
  ));
  private readonly _pendingActivityMap: { [activityId: string]: IActivity };
  initWebsiteProductAction$ = createEffect(() => this._actions$.pipe(
    ofType(websiteProductActions.INIT_WEBSITE_PRODUCT_ACTION),
    mergeMap((action: InitWebsiteProductAction) => {
      const payload = action.payload;
      const productsList: any = action.payload.products.length === 1 && !action.payload.products[0].uid ? action.payload.products[0] : payload.products;
      if (payload.website && +payload.website.id === Config.demoDomainId) {
        return EMPTY;
      }
      this._productActionService.initProductAction(payload.website, productsList, payload.action, payload.network)
        .pipe(filter((response: DbResponse) => !!response),
          first())
        .subscribe((response: DbResponse) => {
          if (!response.success) {
            setTimeout(() => {
              action.payload.products.forEach((product: Product) => {
                const activityId: string = (payload.website.id ? payload.website.id.toString() : payload.website) + product.slug + payload.action;

                delete this._pendingActivityMap[activityId];

                this._store.dispatch(new UpdateOngoingActivityState({
                  websiteId: payload.website.id,
                  entity: this.generateProductFailedActivity(activityId, payload.website, product, payload.action)
                }));
                this._store.dispatch(new FinishOngoingActivity({
                  websiteId: payload.website.id,
                  entity: this.generateProductFailedActivity(activityId, payload.website, product, payload.action)
                }));
              });
            });
          }
        });

      return action.payload.products.map((product: Product) => {
        const activityId: string = (payload.website.id ? payload.website.id.toString() : payload.website) + product.slug + payload.action;
        const activity: IActivity = this.generateProductPendingActivity(activityId, payload.website, product, payload.action);

        this._pendingActivityMap[activityId] = activity;

        return new ongoingActivityActions.UpdateOngoingActivityState({
          websiteId: payload.website.id,
          entity: activity
        });
      });
    })
  ));

  startWebsiteProductActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.INSTALL,
      ActivityAction.INSTALL_ACTIVATE,
      ActivityAction.INSTALL_ACTIVATE_UPLOAD,
      ActivityAction.UPDATE,
      ActivityAction.DEACTIVATE_DELETE,
      ActivityAction.ACTIVATE,
      ActivityAction.DEACTIVATE
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.RUNNING
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + activity.meta.product_data.slug + activity.action;
      return !!this._pendingActivityMap[activityId];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      const activityId: string = activity.websiteId.toString() + activity.meta.product_data.slug + activity.action;

      delete this._pendingActivityMap[activityId];

      if (activity.progress === 0) {
        this.simulateActivityProgress(activity);
      }

      return new ongoingActivityActions.RemoveOngoingActivity({
        websiteId: activity.websiteId,
        activityId: activityId
      });
    })
  ));
  private readonly _simulatingActivityMap: { [activityId: string]: IActivity };
  stopWebsiteProductActivitySimulation$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityAction.INSTALL,
      ActivityAction.INSTALL_ACTIVATE,
      ActivityAction.INSTALL_ACTIVATE_UPLOAD,
      ActivityAction.UPDATE,
      ActivityAction.DEACTIVATE_DELETE,
      ActivityAction.ACTIVATE,
      ActivityAction.DEACTIVATE
    ]).includes(action.payload.action)),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.DONE,
      ActivityStatus.FAILED
    ]).includes(action.payload.status)),
    filter((action: AddActivity): boolean => {
      const activity: IActivity = action.payload;
      return !!this._simulatingActivityMap[activity.id];
    }),
    map((action: AddActivity) => {
      const activity: IActivity = action.payload;
      delete this._simulatingActivityMap[activity.id];

      return new storeActions.Foo();
    })
  ));

  updateWebsiteProductUploadStateActivity$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivityActions.UPDATE_ONGOING_ACTIVITY_STATE),
    filter((action: FinishOngoingActivity): boolean => (<string[]>[
      ActivityAction.INSTALL,
      ActivityAction.INSTALL_ACTIVATE,
      ActivityAction.INSTALL_ACTIVATE_UPLOAD
    ]).includes(action.payload.entity.action) && TypeUtils.isEmpty(action.payload.entity.meta)),
    filter((action: FinishOngoingActivity): boolean => (<string[]>[
      ActivityStatus.DONE,
      ActivityStatus.FAILED
    ]).includes(action.payload.entity.status)),
    switchMap((action: FinishOngoingActivity) => {
      const activity: IActivity = action.payload.entity;

      const simulatingActivityId: string = activity.websiteId.toString() + 'upload' + activity.action;
      delete this._simulatingActivityMap[simulatingActivityId];
      return [
        new ongoingActivityActions.RemoveOngoingActivity({
          websiteId: activity.websiteId,
          activityId: simulatingActivityId
        }),
        new websiteProductActions.LoadWebsiteProducts(activity.websiteId)
      ];
    })
  ));

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions,
    private _productService: ProductService,
    private _productStatusActionService: ProductStatusActionService,
    private _productActionService: ProductActionService
  ) {
    this._pendingActivityMap = {};
    this._simulatingActivityMap = {};
  }

  private simulateActivityProgress(baseActivity: IActivity): void {
    this._simulatingActivityMap[baseActivity.id] = baseActivity;

    const interval = setInterval((): void => {
      const previousActivity: IActivity = this._simulatingActivityMap[baseActivity.id];

      if (!previousActivity || previousActivity.progress >= 98) {
        clearInterval(interval);
        return;
      }

      let nextProgress: number;
      if (previousActivity.progress < 50) {
        nextProgress = previousActivity.progress + 3;
      } else if (previousActivity.progress < 80) {
        nextProgress = previousActivity.progress + 2;
      } else {
        nextProgress = previousActivity.progress + 1;
      }

      const activity: IActivity = <IActivity>{};
      activity.id = previousActivity.id;
      activity.websiteId = previousActivity.websiteId;
      activity.service = previousActivity.service;
      activity.type = previousActivity.type;
      activity.action = previousActivity.action;
      activity.date = new Date();
      activity.status = previousActivity.status;
      activity.progress = nextProgress;
      activity.message = previousActivity.message;
      activity.meta = JSON.parse(JSON.stringify(previousActivity.meta));

      this._simulatingActivityMap[activity.id] = activity;

      this._store.dispatch(new UpdateOngoingActivityState({
        websiteId: activity.websiteId,
        entity: activity
      }));
    }, 100);
  }

  private generateProductPendingActivity(activityId: string, website: Website, product: Product, action: string, progress: number = 0): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = website.id;
    activity.service = 'manager';
    activity.type = ActivityType.ACTIVITY;
    activity.action = action;
    activity.date = new Date();
    activity.status = ActivityStatus.PENDING;
    activity.progress = progress;
    activity.message = '';
    activity.meta = {
      product_data: {
        id: product.productId,
        type: product.type,
        slug: product.slug,
        title: product.title
      }
    };

    return activity;
  }

  private generateProductUploadActivity(activityId: string, website: Website, product: Product, action: string): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = website.id;
    activity.service = 'manager';
    activity.type = ActivityType.ACTIVITY;
    activity.action = action;
    activity.date = new Date();
    activity.status = ActivityStatus.RUNNING;
    activity.progress = 0;
    activity.message = '';
    activity.meta = {};

    return activity;
  }

  private generateProductFailedActivity(activityId: string, website: Website, product: Product, action: string, isUpload: boolean = false): IActivity {
    const activity: IActivity = <IActivity>{};
    activity.id = activityId;
    activity.websiteId = website.id;
    activity.service = 'manager';
    activity.type = ActivityType.ACTIVITY;
    activity.action = action;
    activity.date = new Date();
    activity.status = ActivityStatus.FAILED;
    activity.progress = 100;
    activity.message = '';
    activity.meta = isUpload ? {} : {
      product_data: {
        id: product.productId,
        type: product.type,
        slug: product.slug,
        title: product.title
      }
    };

    return activity;
  }

}
